/** A local tenant selection seeded from the console-wide scope.
 *
 * For the surfaces that keep their tenant in component state rather than in the
 * URL — the RBAC trees, Preflight Items, Blacklists, the notification
 * sub-grids, and every create dialog. They have no URL layer to fall back
 * through, so the scope is simply their starting value.
 *
 * The selection FOLLOWS the console scope until the user picks something here;
 * after that it is theirs and a scope change leaves it alone (clearing it
 * counts — "all tenants, on this page" is a choice). `reset()` hands control
 * back to the scope, which is what a dialog wants when it closes.
 *
 * One tenant: it follows the scope only while the scope names exactly one
 * operator (`useTenantScope`); a scope of several leaves it empty (D5). Lists
 * that take several tenants use `useScopedTenantsState` (`./useScopedTenantsState`).
 */
import { useCallback, useRef, useState } from 'react';

import type { TenantPickerValue } from '@/components/TenantPicker/TenantPicker';

import { useTenantScope } from './tenantScope';
import { useTenantScopeConsumer } from './useTenantScopeConsumer';

export type UseScopedTenantState = [
  value: TenantPickerValue | null,
  setValue: (t: TenantPickerValue | null) => void,
  /** Forget the local choice and go back to following the console scope. */
  reset: () => void,
];

export function useScopedTenantState(seed?: TenantPickerValue | null): UseScopedTenantState {
  const scope = useTenantScope();
  useTenantScopeConsumer();

  const [value, setValueState] = useState<TenantPickerValue | null>(seed ?? scope);
  // A caller-supplied seed is already a deliberate choice (a dialog opened for
  // a known tenant), so it is not overridden by the scope.
  const touched = useRef(seed != null);

  // Follow a scope change during render rather than in an effect — no flash of
  // the old tenant, no extra render (the "adjust state on change" pattern).
  const lastScopeId = useRef<number | null>(scope?.id ?? null);
  const scopeId = scope?.id ?? null;
  if (lastScopeId.current !== scopeId) {
    lastScopeId.current = scopeId;
    if (!touched.current) setValueState(scope);
  }

  const setValue = useCallback((t: TenantPickerValue | null) => {
    touched.current = true;
    setValueState(t);
  }, []);

  const reset = useCallback(() => {
    touched.current = false;
    setValueState(scope);
  }, [scope]);

  return [value, setValue, reset];
}
